import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
} from '@/utils/apiParamsUtils';

/**
 * Searches SEF (System of Electronic Invoices) companies based on optional filters and language preference.
 *
 * @async
 * @function searchAllSefCompanies
 * @param {Object} params - Query parameters for searching SEF companies.
 * @param {Object} [params.filterParams] - Optional filters such as `name`, `nameLatin`, `registrationCode`, `vatRegistrationCode`, and `bugetCompanyNumber`.
 * @param {number} [params.limit=20] - Maximum number of results to retrieve.
 * @param {string} [params.language='en'] - Language of the response (used in the Accept-Language header).
 * @returns {Promise<Array>} The list of matched SEF company objects returned from the server.
 * @throws Will throw an error if the request to search SEF companies fails.
 */
export const searchAllSefCompanies = async ({
  filterParams = {},
  limit = 20,
  language = 'en',
}) => {
  const filters = prepareFilters(filterParams, {
    nameLatin: 'default',
    name: 'default',
    registrationCode: 'default',
    vatRegistrationCode: 'default',
    bugetCompanyNumber: 'default',
  });

  const cleanedFilter = Object.entries(filters).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});

  const hasFilters = Object.keys(cleanedFilter).length > 0;

  try {
    const response = await axiosClient.get('/invoices/sef/companies/search', {
      params: {
        ...(hasFilters ? { filter: cleanedFilter } : {}),
        limit,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        return qs.stringify(params, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
      headers: {
        'Accept-Language': language,
      },
    });

    return response.data.items;
  } catch (error) {
    console.error('Failed to retrieve SEF companies:', error);
    throw error;
  }
};

/**
 * Creates a new customer by sending the provided payload to the server.
 *
 * @async
 * @function createNewCustomer
 * @param {Object} payload - The customer data to be created (e.g. name, address, tax ID).
 * @returns {Promise<Object>} The newly created customer data returned from the server.
 * @throws Will throw an error if the request to create the customer fails.
 */
export const createNewCustomer = async (payload) => {
  try {
    const response = await axiosClient.post('/customers/create', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create company:', error);
    throw error;
  }
};

/**
 * Retrieves a list of customers from the server with optional filtering, sorting, field selection, and pagination.
 *
 * @async
 * @function retrieveAllCustomers
 * @param {Object} params - Query parameters for retrieving customers.
 * @param {Object} [params.filter] - Optional filters for narrowing down the customer list.
 * @param {Object} [params.sorter] - Optional sorting criteria.
 * @param {Object} [params.select] - Optional field selection (projection).
 * @param {Object} [params.pagination] - Optional pagination settings (e.g. page, pageSize).
 * @returns {Promise<Object>} The response data containing the list of customers and pagination metadata.
 * @throws Will throw an error if the request to retrieve customers fails.
 */
export const retrieveAllCustomers = async (params) => {
  const pagination = preparePagination(params);

  try {
    const response = await axiosClient.get('/customers', {
      params: {
        filter: params.filter || {},
        sorter: params.sorter || {},
        select: params.select || {},
        ...pagination,
      },
      paramsSerializer: (params) => {
        if (params.filter) {
          params.filter = JSON.stringify(params.filter);
        }

        if (params.sorter) {
          params.sorter = JSON.stringify(params.sorter);
        }

        if (params.select) {
          params.select = JSON.stringify(params.select);
        }

        return qs.stringify(params, {
          encode: false,
          arrayFormat: 'brackets',
        });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve customers:', error);
    throw error;
  }
};

/**
 * Retrieves a list of customers from the server with optional filtering, sorting, field selection, and pagination.
 *
 * Implementation detail:
 * - Keeps `company` in the app/query params
 *   but remaps it to `userCompany` **only** in the request payload sent to the backend.
 * - Backend should ideally accept both `userCompany` and `company` for safety.
 *
 * @async
 * @function retrieveAllCustomersNew
 * @param {Object} params - Query parameters for retrieving customers.
 * @param {Object} [params.filter] - Optional filters for narrowing down the customer list.
 * @param {Object} [params.sorter] - Optional sorting criteria.
 * @param {Object} [params.select] - Optional field selection (projection).
 * @param {Object} [params.pagination] - Optional pagination settings (e.g. page, pageSize).
 * @returns {Promise<Object>} The response data containing the list of customers and pagination metadata.
 * @throws Will throw an error if the request to retrieve customers fails.
 */
export const retrieveAllCustomersNew = async (params) => {
  // TODO: zameniti sa retrieveAllCustomers kada se oslobodi InvoiceForm

  const filters = prepareFilters(params, {
    company: 'default',
    customerName: 'default',
    vatRegistrationCode: 'default',
    companyNumber: 'default',
  });

  // --- remap: company -> userCompany (request payload only) ---
  // Keep `company` in the app state / URL, but send `userCompany` to backend.
  const apiFilters = { ...filters };
  if (apiFilters.company && !apiFilters.userCompany) {
    apiFilters.userCompany = apiFilters.company;
    delete apiFilters.company;
  }
  // ------------------------------------------------------------

  const sorters = prepareSorters(params, {
    customerName: 'sortCustomerName',
    updatedAt: 'sortUpdatedAt',
  });

  const pagination = preparePagination(params);

  try {
    const response = await axiosClient.get('/customers', {
      params: {
        filter: apiFilters,
        sorter: sorters,
        select: params.select || {},
        ...pagination,
      },
      paramsSerializer: (p) => {
        if (p.filter) p.filter = JSON.stringify(p.filter);
        if (p.sorter) p.sorter = JSON.stringify(p.sorter);
        if (p.select) p.select = JSON.stringify(p.select);

        return qs.stringify(p, {
          encode: false,
          arrayFormat: 'brackets',
        });
      },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve customers:', error);
    throw error;
  }
};

/**
 * Retrieves a single customer by ID from the server, with optional query parameters.
 *
 * @async
 * @function getCustomerById
 * @param {string} customerId - The unique identifier of the customer to retrieve.
 * @param {Object} [params] - Optional query parameters (e.g. `graph` for deep population).
 * @returns {Promise<Object>} The customer data returned from the server.
 * @throws Will throw an error if the request to retrieve the customer fails.
 */
export const getCustomerById = async (customerId, params) => {
  try {
    const response = await axiosClient.get(`/customers/${customerId}`, {
      params,
      paramsSerializer: (params) => {
        if (params.graph) {
          params.graph = JSON.stringify(params.graph);
        }

        return qs.stringify(params, { encode: false, arrayFormat: 'brackets' });
      },
    });
    const { data } = response.data;
    return data;
  } catch (error) {
    console.error(`Failed to retrieve customer with ID ${customerId}:`, error);
    throw error;
  }
};

/**
 * Updates an existing user by their ID with the provided data.
 * @param {string} userId - The unique identifier of the user.
 * @param {Object} payload - The updated data for the user.
 * @returns {Promise<Object>} A promise that resolves with the updated user data.
 * @throws Will throw an error if the update request fails.
 */
export const updateCustomerById = async (customerId, payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await axiosClient.put(
      `/customers/${customerId}`,
      payload,
      {
        headers: isFormData ? {} : { 'Content-Type': 'application/json' },
      }
    );

    return response.data;
  } catch (error) {
    console.error(`Failed to update customer with ID ${customerId}:`, error);
    throw error;
  }
};

/**
 * Deletes a customer by their ID.
 * @param {string} customerId - The unique identifier of the customer to delete.
 * @returns {Promise<Object>} A promise that resolves with the API response for the deletion.
 * @throws Will throw an error if the delete request fails.
 */
export const deleteCustomerById = async (customerId) => {
  try {
    const response = await axiosClient.delete(`/customers/${customerId}`);

    return response.data;
  } catch (error) {
    console.error(`Failed to delete customer with ID ${customerId}:`, error);
    throw error;
  }
};

const CustomersService = {
  searchAllSefCompanies,
  createNewCustomer,
  retrieveAllCustomers,
  getCustomerById,
  updateCustomerById,
  retrieveAllCustomersNew,
  deleteCustomerById
};

export default CustomersService;
